import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

const TRANSPARENT = 'transparent';
const CURSOR_NOT_ALLOWED = 'not-allowed';
const CURSOR_POINTER = 'pointer';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

type ContainerProps = {
  $collapsed?: boolean;
  $inline?: boolean;
};

export const Container = styled.div<ContainerProps>`
  ${({ theme, $collapsed, $inline }) => css`
    background: ${theme.colors.background[100]};
    display: flex;
    flex-direction: column;
    transition: width 0.3s ease;

    ${$inline
      ? css`
          position: relative;
          width: 100%;
          height: 100%;
          border-left: none;
        `
      : css`
          position: fixed;
          top: ${theme.layout.header};
          right: 0;
          bottom: 0;
          width: ${$collapsed
            ? theme.layout.interactionPanelCollapsed
            : theme.layout.interactionPanel};
          border-left: 1px solid ${theme.colors.background[40]};
          z-index: 20;

          ${down('lg')} {
            display: none;
          }
        `}
  `}
`;

export const ToggleButton = styled.button`
  ${({ theme }) => css`
    position: absolute;
    top: -1px;
    bottom: 0;
    left: calc(-1.5rem - 1px);
    width: 1.5rem;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: #fff;
    border: 1px solid ${theme.colors.background[40]};
    border-right: none;
    border-radius: 0.25rem 0 0 0.25rem;
    z-index: 25;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: ${theme.colors.background[80]};
    }

    svg {
      width: 0.75rem;
      height: 0.75rem;
      color: ${theme.colors.foreground[40]};
    }

    ${down('lg')} {
      display: none;
    }
  `}
`;

export const HeaderWrapper = styled.div`
  position: relative;
`;

export const CollapseTab = styled.button`
  ${({ theme }) => css`
    position: absolute;
    top: -1px;
    bottom: 0;
    left: calc(-1.5rem - 1px);
    width: 1.5rem;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: #fff;
    border: 1px solid ${theme.colors.background[40]};
    border-right: none;
    border-radius: 0.25rem 0 0 0.25rem;
    z-index: 25;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
      width: 0.75rem;
      height: 0.75rem;
      color: ${theme.colors.foreground[40]};
    }

    &:hover {
      background: ${theme.colors.background[80]};
    }

    ${down('lg')} {
      display: none;
    }
  `}
`;

export const CollapsedHeader = styled.div`
  ${({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid ${theme.colors.background[40]};
  `}
`;

export const CollapsedTabs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  gap: 0.5rem;
  flex: 1;
`;

type CollapsedTabProps = {
  $active?: boolean;
  $disabled?: boolean;
};

const getHoverBackground = (
  theme: {
    colors: {
      brand: { primary: { opaque: string } };
      background: { 60: string };
    };
  },
  $active?: boolean,
  $disabled?: boolean,
) => {
  if ($disabled) return TRANSPARENT;
  if ($active) return theme.colors.brand.primary.opaque;
  return theme.colors.background[60];
};

export const CollapsedTab = styled.button<CollapsedTabProps>`
  ${({ theme, $active, $disabled }) => css`
    width: 2.5rem;
    height: 2.5rem;
    background: ${$active ? theme.colors.brand.primary.opaque : TRANSPARENT};
    border: none;
    border-radius: ${theme.radii.md};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${$disabled ? CURSOR_NOT_ALLOWED : CURSOR_POINTER};
    transition: all ${theme.transition.default};
    opacity: ${$disabled ? 0.4 : 1};

    svg {
      color: ${$active
        ? theme.colors.brand.primary.default
        : theme.colors.foreground[40]};
    }

    &:hover {
      background: ${getHoverBackground(theme, $active, $disabled)};
    }
  `}
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const TabsBar = styled.div`
  ${({ theme }) => css`
    display: flex;
    padding: 0.5rem;
    gap: 0.25rem;
    background: ${theme.colors.background[80]};
    border-bottom: 1px solid ${theme.colors.background[40]};
  `}
`;

type TabProps = {
  $active?: boolean;
  $disabled?: boolean;
};

export const Tab = styled.button<TabProps>`
  ${({ theme, $active, $disabled }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    background: ${$active ? theme.colors.brand.primary.opaque : TRANSPARENT};
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[40]};
    border: none;
    border-radius: ${theme.radii.default};
    font-size: ${theme.fontSizes.xs};
    font-weight: 500;
    cursor: ${$disabled ? CURSOR_NOT_ALLOWED : CURSOR_POINTER};
    transition: all ${theme.transition.default};
    white-space: nowrap;
    opacity: ${$disabled ? 0.4 : 1};

    &:hover {
      background: ${getHoverBackground(theme, $active, $disabled)};
    }

    svg {
      flex-shrink: 0;
    }
  `}
`;

export const MoreTab = styled.div<TabProps>`
  ${({ theme, $active }) => css`
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    background: ${$active ? theme.colors.brand.primary.opaque : TRANSPARENT};
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[40]};
    border: none;
    border-radius: ${theme.radii.default};
    font-size: ${theme.fontSizes.xs};
    font-weight: 500;
    cursor: pointer;
    transition: all ${theme.transition.default};

    &:hover {
      background: ${$active
        ? theme.colors.brand.primary.opaque
        : theme.colors.background[60]};
    }
  `}
`;

export const Dropdown = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: calc(100% + 0.5rem);
    right: 0;
    min-width: 10rem;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    border-radius: ${theme.radii.default};
    box-shadow: ${theme.shadows.lg};
    z-index: 100;
    overflow: hidden;
    animation: ${slideIn} 0.2s ease-out forwards;
  `}
`;

export const DropdownItem = styled.button<TabProps>`
  ${({ theme, $active, $disabled }) => css`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    background: ${$active ? theme.colors.brand.primary.opaque : TRANSPARENT};
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[60]};
    border: none;
    font-size: ${theme.fontSizes.sm};
    cursor: ${$disabled ? CURSOR_NOT_ALLOWED : CURSOR_POINTER};
    transition: background ${theme.transition.default};
    opacity: ${$disabled ? 0.4 : 1};

    &:hover {
      background: ${$disabled ? TRANSPARENT : theme.colors.background[60]};
    }

    svg {
      flex-shrink: 0;
    }
  `}
`;

export const Header = styled.div`
  ${({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${theme.colors.background[40]};
  `}
`;

export const HeaderTitle = styled.h3`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.sm};
    font-weight: 500;
    color: ${theme.colors.foreground[80]};
    margin: 0;
  `}
`;

export const OnlineBadge = styled.span`
  ${({ theme }) => css`
    padding: 0.25rem 0.625rem;
    background: ${theme.colors.success[40]};
    color: ${theme.colors.success[100]};
    font-size: ${theme.fontSizes.xs};
    font-weight: 500;
    border-radius: ${theme.radii.full};
  `}
`;

export const MessagesContainer = styled.div`
  ${({ theme }) => css`
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &::-webkit-scrollbar {
      width: 0.375rem;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.background[40]};
      border-radius: ${theme.radii.full};
    }
  `}
`;

export const Message = styled.div`
  display: flex;
  gap: 0.75rem;
  animation: ${slideIn} 0.3s ease-out forwards;
`;

export const Avatar = styled.div`
  ${({ theme }) => css`
    width: 2rem;
    height: 2rem;
    background: linear-gradient(
      135deg,
      ${theme.colors.brand.primary.default},
      ${theme.colors.brand.secondary.default}
    );
    border-radius: ${theme.radii.full};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${theme.fontSizes.xs};
    font-weight: 700;
    color: white;
    flex-shrink: 0;
  `}
`;

export const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

export const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const UserName = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.sm};
    font-weight: 500;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const Time = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.xs};
    color: ${theme.colors.foreground[40]};
  `}
`;

export const MessageText = styled.p`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.foreground[60]};
    margin: 0;
    line-height: 1.4;
    word-break: break-word;
  `}
`;

export const PlaceholderContent = styled.div`
  ${({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: ${theme.colors.foreground[40]};

    svg {
      opacity: 0.5;
    }

    span {
      font-size: ${theme.fontSizes.sm};
    }
  `}
`;

export const InputContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid ${theme.colors.background[40]};
  `}
`;

export const Input = styled.input`
  ${({ theme }) => css`
    flex: 1;
    padding: 0.625rem 1rem;
    background: ${theme.colors.background[60]};
    border: none;
    border-radius: ${theme.radii.md};
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.foreground[100]};
    outline: none;
    transition: all ${theme.transition.default};

    &::placeholder {
      color: ${theme.colors.foreground[40]};
    }

    &:focus {
      background: ${theme.colors.background[100]};
      box-shadow: 0 0 0 2px ${theme.colors.brand.primary.default};
    }
  `}
`;

export const SendButton = styled.button`
  ${({ theme }) => css`
    width: 2.5rem;
    height: 2.5rem;
    background: ${theme.colors.brand.primary.default};
    border: none;
    border-radius: ${theme.radii.md};
    color: ${theme.colors.brand.primary.text};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all ${theme.transition.default};
    box-shadow: ${theme.shadows.md};

    &:hover:not(:disabled) {
      background: ${theme.colors.brand.primary.hover};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `}
`;

export const TabContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const DisabledContent = styled.div`
  ${({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    color: ${theme.colors.foreground[40]};
    text-align: center;

    span {
      font-size: ${theme.fontSizes.sm};
    }
  `}
`;
